import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Lock, KeyRound, UserCheck, FileCheck2, Database,
  UserCog, Terminal, Activity, ArchiveRestore, AlertTriangle,
  CloudCheck, CheckCircle2, RefreshCw, ChevronRight, History, ShieldAlert, ArrowRight
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import SEO from "@/components/SEO";

const SECTIONS = [
  { id: "objectives", title: "1. Security Objectives", icon: ShieldCheck },
  { id: "access-control", title: "2. Access Control", icon: KeyRound },
  { id: "authentication", title: "3. Authentication", icon: Lock },
  { id: "isolation", title: "4. Client-Level Data Isolation", icon: UserCheck },
  { id: "documents", title: "5. Document Security", icon: FileCheck2 },
  { id: "encryption", title: "6. Encryption", icon: Database },
  { id: "employees", title: "7. Employee Responsibilities", icon: UserCog },
  { id: "admin-access", title: "8. Administrative Access", icon: Terminal },
  { id: "monitoring", title: "9. Monitoring & Audit Logs", icon: Activity },
  { id: "backup", title: "10. Backup & Recovery", icon: ArchiveRestore },
  { id: "incident-management", title: "11. Security Incident Management", icon: AlertTriangle },
  { id: "third-party", title: "12. Third-Party Service Providers", icon: CloudCheck },
  { id: "client-responsibilities", title: "13. Client Responsibilities", icon: ShieldAlert },
  { id: "continuous-improvement", title: "14. Continuous Improvement", icon: RefreshCw },
];

export default function DataSecurityPolicy() {
  const [activeSection, setActiveSection] = useState("objectives");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 140;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const navOffset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-navy-900 antialiased" data-testid="data-security-page">
      <SEO
        title="Data Security Policy | Biz360 Compliance"
        description="Data Security Policy describing enterprise encryption, role-based access controls, client data isolation, and incident response at Biz360 Compliance."
        canonical="/data-security-policy"
        breadcrumbs={[{ label: "Data Security Policy", href: "/data-security-policy" }]}
      />
      <ScrollProgress />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-14 md:pt-40 md:pb-16 border-b border-slate-200">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="data-security-breadcrumb">
                <Link to="/" className="hover:text-navy-800 transition-colors">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-navy-700">Data Security Policy</span>
              </div>
            </Reveal>

            <div className="text-center">
              <Reveal delay={0.05}>
                <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                  Enterprise Data Protection
                </span>
              </Reveal>

              <Reveal delay={0.1}>
                <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-900 md:text-5xl">
                  DATA SECURITY POLICY
                </h1>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/80 px-4 py-1.5 text-xs font-medium text-slate-600 shadow-sm border border-slate-200">
                  <History className="h-3.5 w-3.5 text-gold-600" />
                  <span>Last Updated: <strong className="text-navy-900">01/09/2026</strong></span>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-navy-900/10 bg-white/90 p-6 text-left shadow-sm backdrop-blur-sm">
                  <p className="text-base font-semibold text-navy-900">
                    Biz360 Compliance recognises that financial, tax, accounting, identity and business information entrusted to us requires appropriate protection.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    This Data Security Policy describes the broad security controls adopted by Biz360 to protect information handled through our website, CRM, client portal and service operations.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Content Container */}
        <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            
            {/* Quick Navigation Sticky Sidebar */}
            <aside className="hidden lg:col-span-4 lg:block">
              <div className="sticky top-28 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm max-h-[calc(100vh-140px)] overflow-y-auto">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-2">
                  Table of Contents
                </h3>
                <nav className="space-y-1">
                  {SECTIONS.map((sec) => {
                    const SIcon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        onClick={() => scrollToSection(sec.id)}
                        className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-xs font-medium transition-all ${
                          isActive
                            ? "bg-navy-900 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                        }`}
                      >
                        <SIcon className={`h-4 w-4 shrink-0 ${isActive ? "text-gold-400" : "text-slate-400"}`} />
                        <span className="truncate">{sec.title}</span>
                      </button>
                    );
                  })}
                </nav>

                <div className="mt-6 border-t border-slate-100 pt-5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 px-2">
                    Related Policies
                  </h4>
                  <div className="space-y-1.5 text-xs font-medium">
                    <Link
                      to="/terms"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Terms & Conditions</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/privacy-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Privacy Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/data-retention-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Data Retention Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Articles Content */}
            <div className="space-y-8 lg:col-span-8">

              {/* 1. Security Objectives */}
              <article id="objectives" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">1. Security Objectives</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Our security programme aims to protect information against:
                </p>
                <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Unauthorised access",
                    "Unauthorised disclosure",
                    "Accidental loss",
                    "Unauthorised alteration",
                    "Misuse",
                    "Destruction",
                    "Service disruption"
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2.5 rounded-xl bg-slate-50 p-3 border border-slate-200/70">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span className="font-medium text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 2. Access Control */}
              <article id="access-control" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <KeyRound className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">2. Access Control</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Access to information is provided based on role and business requirement.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">Our system may use:</p>
                <ul className="mt-2 space-y-2 text-sm text-slate-700">
                  {[
                    "Admin access;",
                    "Employee/Sub-admin access;",
                    "Client access;",
                    "Assignment-level access;",
                    "Service-level permissions."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/70 p-3.5 text-xs font-semibold text-blue-950">
                  Employees are provided access only to information reasonably required for their assigned responsibilities.
                </div>
              </article>

              {/* 3. Authentication */}
              <article id="authentication" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Lock className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">3. Authentication</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Depending on system functionality, security measures may include:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Strong passwords;",
                    "Multi-factor authentication;",
                    "OTP verification;",
                    "Session management;",
                    "Login monitoring; and",
                    "Account lockout or other protective mechanisms."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-navy-800 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Users are responsible for keeping their login credentials confidential.
                </p>
              </article>

              {/* 4. Client-Level Data Isolation */}
              <article id="isolation" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <UserCheck className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">4. Client-Level Data Isolation</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700 font-medium">
                  Client information is logically separated within the system.
                </p>
                <ul className="mt-3 space-y-2.5 text-sm text-slate-700">
                  <li className="flex items-start gap-2.5">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                    <span>An employee should only be able to access clients, assignments and documents allocated or otherwise authorised to them.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                    <span>A customer can access only information belonging to their own account/organisation, subject to authorised user permissions.</span>
                  </li>
                </ul>
              </article>

              {/* 5. Document Security */}
              <article id="documents" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <FileCheck2 className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">5. Document Security</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Documents uploaded to the platform may contain sensitive financial or personal information. Appropriate measures may include:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Restricted access;",
                    "Secure storage;",
                    "Controlled downloads;",
                    "Access logging;",
                    "File validation;",
                    "Backup controls; and",
                    "Controlled deletion."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 6. Encryption */}
              <article id="encryption" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Database className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">6. Encryption</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Where technically appropriate, encryption or equivalent security controls may be used for:
                </p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Data transmission",
                    "Stored information",
                    "Authentication credentials",
                    "Backup information"
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 rounded-lg bg-slate-50 p-2.5 border border-slate-200/70">
                      <span className="h-2 w-2 rounded-full bg-blue-600 shrink-0" />
                      <span className="text-xs font-semibold text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 7. Employee Responsibilities */}
              <article id="employees" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <UserCog className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">7. Employee Responsibilities</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700 font-semibold">
                  Employees and authorised users must:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {[
                    "Keep credentials confidential;",
                    "Access only authorised information;",
                    "Avoid downloading information unnecessarily;",
                    "Not share client documents through unauthorised channels;",
                    "Report suspected security incidents promptly; and",
                    "Follow internal access and information-handling procedures."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 8. Administrative Access */}
              <article id="admin-access" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Terminal className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">8. Administrative Access</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Administrative privileges are restricted to authorised personnel.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">Important administrative actions may be logged, including:</p>
                <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "User creation;",
                    "Permission changes;",
                    "Client allocation;",
                    "Data modification;",
                    "Deletion actions; and",
                    "System configuration changes."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 9. Monitoring & Audit Logs */}
              <article id="monitoring" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Activity className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">9. Monitoring & Audit Logs</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  The system may maintain logs relating to:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Login attempts;",
                    "User activity;",
                    "Document access;",
                    "Data changes;",
                    "Administrative activity;",
                    "Security events."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs font-medium text-slate-500 italic">
                  Logs may be retained for security, operational, compliance and investigation purposes.
                </p>
              </article>

              {/* 10. Backup & Recovery */}
              <article id="backup" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ArchiveRestore className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">10. Backup & Recovery</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Reasonable backup and recovery procedures are maintained for important business information.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">Backups may be used to restore information following:</p>
                <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Technical failure;",
                    "Accidental deletion;",
                    "System outage;",
                    "Security incident; or",
                    "Other disruptive events."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 11. Security Incident Management */}
              <article id="incident-management" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <AlertTriangle className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">11. Security Incident Management</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700 font-semibold">
                  Where a security incident is identified:
                </p>
                <ol className="mt-3 space-y-2.5 text-sm text-slate-700">
                  {[
                    "The incident will be assessed.",
                    "Access may be restricted where necessary.",
                    "The affected systems/data may be secured.",
                    "Investigation and corrective action may be undertaken.",
                    "Notifications will be made where required by applicable law."
                  ].map((step, idx) => (
                    <li key={step} className="flex items-start gap-3">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-navy-900 text-xs font-bold text-white">
                        {idx + 1}
                      </span>
                      <span className="pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
              </article>

              {/* 12. Third-Party Service Providers */}
              <article id="third-party" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <CloudCheck className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">12. Third-Party Service Providers</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Biz360 may use third-party providers for hosting, cloud storage, payment processing, communication, analytics and other technical services.
                </p>
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-xs font-semibold text-slate-700">
                  Relevant providers are expected to maintain appropriate security measures for the services they provide.
                </div>
              </article>

              {/* 13. Client Responsibilities */}
              <article id="client-responsibilities" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ShieldAlert className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">13. Client Responsibilities</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700 font-semibold">
                  Clients should:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {[
                    "Use strong passwords;",
                    "Avoid sharing login credentials;",
                    "Keep devices secure;",
                    "Verify communication requesting sensitive information;",
                    "Notify Biz360 of suspicious activity; and",
                    "Avoid uploading unnecessary personal information."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 14. Continuous Improvement */}
              <article id="continuous-improvement" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <RefreshCw className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">14. Continuous Improvement</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Security controls may be reviewed and improved periodically based on:
                </p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Technology changes",
                    "Business requirements",
                    "Security incidents",
                    "Risk assessments",
                    "Applicable legal requirements"
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 rounded-lg bg-slate-50 p-2.5 border border-slate-200/70">
                      <span className="h-2 w-2 rounded-full bg-emerald-600 shrink-0" />
                      <span className="text-xs font-semibold text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
